import { debounce } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { isInGeotarget, getLocationInfo, getIntlMinimumShippingThreshold } from './location';
import { notNil } from './typescriptUtils';
import { CurrencyState } from './currency';
import { ShippingCountryState } from './shipping';

// [IMPROVEMENT] could be adapted to support []s, & options (logging, etc.)
export const safeUpdateHtmlNodeAttributes = (selector: string, attributes: Partial<HTMLElement>, options = {}) => {
  document.querySelectorAll(selector).forEach((node) => {
    Object.assign(node, attributes);
  });
};
enum GEOTARGET_MODE {
  Allowlist = 'allowlist',
  Denylist = 'denylist',
}

export const findGeotargetedElements = async (options: {
  selector?: string;
  callback: (values: {
    element: HTMLElement;
    matchesGeotarget: boolean;
    countries: string[] | undefined;
    usZips: string[] | undefined;
    mode: GEOTARGET_MODE;
  }) => void;
}) => {
  const { selector = '[geotarget_is_enabled=true]', callback } = options || {};

  const geoTargetedBlocks = document.querySelectorAll<HTMLElement>(selector);

  for (const element of geoTargetedBlocks) {
    const geotarget_mode = element.getAttribute('geotarget_mode');
    const geotarget_countries = element.getAttribute('geotarget_countries');
    const geotarget_us_zipcodes = element.getAttribute('geotarget_us_zipcodes');
    const [countries, usZips] = [geotarget_countries, geotarget_us_zipcodes].map(
      (attribute) => attribute?.split(',').map((item) => item.trim()) || undefined
    );

    const matchesGeotarget = await isInGeotarget({ countries, usZips });
    callback({
      element,
      matchesGeotarget,
      countries,
      usZips,
      mode:
        notNil(geotarget_mode) &&
        [GEOTARGET_MODE.Allowlist, GEOTARGET_MODE.Denylist].some((mode) => mode === geotarget_mode)
          ? (geotarget_mode as GEOTARGET_MODE)
          : GEOTARGET_MODE.Allowlist, // ? [REVISIT] default to allowlist? or null?
    });
  }
};

export const updateGeotargetedElements = async (options?: { selector?: string }) =>
  findGeotargetedElements({
    ...options,
    callback: (values) => {
      const { element, matchesGeotarget, mode } = values;
      const shouldRemove = mode === 'allowlist' ? !matchesGeotarget : matchesGeotarget;
      element.style.visibility = '';
      element.style.display = '';
      if (shouldRemove) element.remove();
    },
  });

export const overrideInternationalShippingBanner = async () => {
  if (ShippingCountryState.countryCode === 'US') return;
  if (!ShippingCountryState.countryCode && (await isInGeotarget({ countries: ['US'] }))) return;
  const shippingBannerElements = document.querySelectorAll('.international-shipping-message-override');
  const intlMinimumShippingThreshold = getIntlMinimumShippingThreshold(CurrencyState.currencyCode);

  shippingBannerElements?.forEach((shippingBannerElement) => {
    shippingBannerElement.innerHTML = `<strong>Free Shipping over ${intlMinimumShippingThreshold}</strong>`;
  });
};

// ? Should this be in 'dom' or in 'location', it's a bit of both; we can relocate as needed
/**
 * Updates visibility of geotargeted elements. solves the problem generically, could be used for any element.
 */
export const updateGeotargetedElementDisplay = async (options?: { selector?: string }) =>
  findGeotargetedElements({
    ...options,
    callback: ({ element, matchesGeotarget, mode }) => {
      // flexibly allows first paint to display none OR not, either way we will deal with it
      element.style.display = (mode === GEOTARGET_MODE.Allowlist ? matchesGeotarget : !matchesGeotarget)
        ? 'inherit'
        : 'none';
    },
  });

export const addTagsToEmailSignupForms = async (options?: { selector: string }) => {
  const { selector = '.email-form-dynamic-tags' } = options || {}; // TS 4.1?
  const fields = document.querySelectorAll<HTMLInputElement>(selector);
  if (fields.length === 0) {
    return;
  }

  const tags = await getTagsForEmailSignUp();

  fields.forEach(function (f) {
    if (f.value) {
      f.value = `${f.value},${tags.join(',')}`;
    } else {
      f.value = tags.join(',');
    }
  });
};

export const getTagsForEmailSignUp = async () => {
  const locationInfo = await getLocationInfo();

  const tags: string[] = [];
  tags.push(`cc:${locationInfo.country_code}`);
  tags.push(`rc:${locationInfo.country_code}${locationInfo.region_code}`);
  if (
    locationInfo.country_code.toUpperCase() === 'US' &&
    'string' === typeof locationInfo.zip &&
    locationInfo.zip.length === 5
  ) {
    tags.push(`us-metro-${locationInfo.zip.slice(0, 2)}`);
  }

  return tags;
};

export const domElementFromRawHTML = (domstring: string): HTMLElement => {
  var html = new DOMParser().parseFromString(domstring, 'text/html');
  return html.body.firstChild as HTMLElement;
};

export function insertAfter(newNode: HTMLElement, existingNode: HTMLElement | null | undefined) {
  existingNode?.parentNode?.insertBefore(newNode, existingNode.nextSibling);
}

export const getStickElementsOffsetHeight = () => {
  const STICK_ELEMENT_SELECTORS = ['.sticky-atc-block', '#shopify-section-main-nav'];
  const heights = STICK_ELEMENT_SELECTORS.map((selector) => {
    const element = document.querySelector(selector);
    const style = element && window.getComputedStyle(element);

    const isStickyElement = style?.position === 'sticky' || style?.position === 'fixed';
    return element && isStickyElement ? element.getBoundingClientRect().height : 0;
  });
  return heights.reduce((a, b) => a + b);
};

export const useOldStickyFiltersLogicForDynamicHeaderHeightCalculation = ({
  cssSelectorForDynamicStickyHeightAdjustment,
}: {
  cssSelectorForDynamicStickyHeightAdjustment: string;
}) => {
  const wrapperNodeRef: React.RefObject<HTMLDivElement> = useRef(null);
  const debouncedAdjustHeight = useRef(
    debounce(
      () => {
        if (wrapperNodeRef.current !== undefined) {
          //@ts-expect-error
          const targetNode: HTMLElement | undefined = wrapperNodeRef.current.querySelector(
            cssSelectorForDynamicStickyHeightAdjustment
          );
          if (targetNode) {
            const heightOfChildren = Array.from(targetNode.children).reduce(
              (total, i) => total + i?.getBoundingClientRect().height,
              0
            );
            const boundingRect = targetNode.getBoundingClientRect();
            const newMaxHeight = Math.floor(window.innerHeight - boundingRect.top) - 20;
            const isScrollOverflowNecessary = heightOfChildren >= newMaxHeight;
            targetNode.style.maxHeight = isScrollOverflowNecessary ? newMaxHeight + 'px' : '';
            targetNode.style.minHeight = isScrollOverflowNecessary ? newMaxHeight + 'px' : '';
            targetNode.style.overflowY = isScrollOverflowNecessary ? 'auto' : '';
          }
        }
      },
      250
      // adjust debounce options w/ the optional 3rd param
    )
  ).current;
  useEffect(() => {
    window.addEventListener('scroll', debouncedAdjustHeight);
    return () => {
      window.removeEventListener('scroll', debouncedAdjustHeight);
    };
    // ? [REVISIT]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export function stickyPLPFilterControls() {
  const sortElement = document.querySelector('.alo-dropdown.sorting .MuiAutocomplete-root');
  const element = document.querySelector('.sticky-list-controls');
  if (!element) return;

  const getStickyTop = () => {
    const uniBannerWrapper = document.querySelector('.sticky-uni-banner');
    const mainNav = document.getElementById('shopify-section-main-nav');
    return (mainNav?.offsetHeight || 0) + (uniBannerWrapper?.offsetHeight || 0) + 'px';
  };

  let lastScroll = 0;
  let stickyTop = getStickyTop();
  let desktopView = window.outerWidth > 991;

  window.addEventListener('scroll', (e) => {
    const currentScroll = window.pageYOffset;
    if (currentScroll === lastScroll) return;
    if (currentScroll <= 0 || desktopView) {
      element.style.top = '-' + stickyTop;
      return;
    }
    if (currentScroll > lastScroll) {
      // down
      element.style.top = '-' + stickyTop;
      if (sortElement && sortElement.getAttribute('aria-expanded') === 'true') {
        const sortButton = document.querySelector('button.MuiIconButton-root');
        sortButton && sortButton.click();
      }
    } else if (currentScroll < lastScroll) element.style.top = stickyTop; // up
    lastScroll = currentScroll;
  });

  window.addEventListener('resize', () => {
    desktopView = window.outerWidth > 991;
    stickyTop = getStickyTop();
  });
}
